import { LinearProgress, makeStyles, Paper, Theme } from "@material-ui/core";
import { CloudUpload } from "@material-ui/icons";
import * as React from "react";
import Dropzone, { DropzoneRenderArgs, FileWithPreview } from "react-dropzone";
import { ApiResponse, customFetch } from "../../api/CustomFetch";
import { useIntl } from "react-intl";

type UploadDropZoneProps = {
  uploadURL?: string;
  onFileUploaded?: (file: FileWithPreview, response: ApiResponse<any>) => void;
  onDrop?: (file: FileWithPreview[]) => void;
  accept?: string;
  tags?: string[];
  allowMultiple?: boolean;
  onUploadError?: (response?: ApiResponse<any>) => void;
  postFileFn?: (file: FileWithPreview) => Promise<any>;
};

const useStyles = makeStyles((theme: Theme) => ({
  dropZone: {
    position: "relative",
    width: "100%",
    height: "200px",
    borderWidth: "2px",
    borderColor: "#888",
    borderStyle: "dashed",
    borderRadius: "5px",
    marginBottom: theme.spacing(1),
  },
  paperRoot: {
    ...theme.mixins.gutters(),
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(0),
    textAlign: "center",
    width: "100%",
    height: "100%",
    boxShadow: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paperRootRejecting: {
    backgroundColor: "rgba(153, 0, 0,0.25)",
  },
  paperRootAccepting: {
    backgroundColor: "rgba(102, 169, 47, 0.35)",
  },
  uploadIcon: {
    height: "2em",
    width: "2em",
  },
}));

const UploadDropZone = (props: UploadDropZoneProps) => {
  const [isInTransfer, setIsInTransfer] = React.useState(false);
  const classes = useStyles();
  const intl = useIntl();

  const { uploadURL, onFileUploaded, onDrop, accept, tags, allowMultiple, onUploadError, postFileFn } = props;

  const MAX_UPLOAD_FILE_SIZE_BYTES = 20 * 1024 * 1024; // 20MB

  const uploadFile = (accepted: FileWithPreview[], rejected: FileWithPreview[]) => {
    if (!accepted || accepted.length === 0) {
      return;
    }

    setIsInTransfer(true);

    const fileToUpload = accepted[0];

    postFile(fileToUpload)
      .then((response) => {
        if (response.ok) {
          setIsInTransfer(false);
          response.json().then((jsonResponse: ApiResponse<any>) => {
            if (onFileUploaded) {
              onFileUploaded(fileToUpload, jsonResponse);
            }
          });
        } else {
          setIsInTransfer(false);
          response.json().then((jsonResponse: ApiResponse<any>) => {
            if (onUploadError) {
              onUploadError(jsonResponse);
            }
          });
        }
      })
      .catch((_) => {
        setIsInTransfer(false);
      });
  };

  const postFile = (file: FileWithPreview): Promise<Response> => {
    if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
      const errorMessage = intl.formatMessage(
        { id: "common.uploadSizeError" },
        { size: MAX_UPLOAD_FILE_SIZE_BYTES / (1024 * 1024) }
      );
      const errorResponse = {
        status: 413,
        errors: [errorMessage],
        data: null,
      } as unknown as ApiResponse<any>;
      if (onUploadError) {
        onUploadError(errorResponse);
      }
      return Promise.reject(new Error(errorMessage));
    }

    if (postFileFn) {
      return postFileFn(file);
    }

    if (!uploadURL) {
      throw new Error("No uploadURL defined for UploadDropZone.");
    }

    const formData = new FormData();
    formData.append("file", file);

    if (tags) {
      tags.forEach((tag: string, index: number) => formData.append(`tags[${index}]`, tag));
    }

    return customFetch(uploadURL, {
      body: formData,
      headers: {
        Accept: "application/json",
      },
      method: "POST",
    });
  };

  return (
    <React.Fragment>
      <Dropzone accept={accept} multiple={!!allowMultiple} onDrop={onDrop || uploadFile} disabled={isInTransfer} className={classes.dropZone}>
        {(childProps: DropzoneRenderArgs) => {
          let paperStyle = classes.paperRoot + " ";
          paperStyle += childProps.isDragReject ? classes.paperRootRejecting : "";
          paperStyle += childProps.isDragAccept ? classes.paperRootAccepting : "";

          return (
            <Paper className={paperStyle}>
              <CloudUpload className={classes.uploadIcon} />
            </Paper>
          );
        }}
      </Dropzone>
      {isInTransfer && <LinearProgress variant="query" />}
    </React.Fragment>
  );
};

export default UploadDropZone;
